import React, { useEffect, useState } from 'react'
import {
  Table,
  Button,
  Space,
  Input,
  Select,
  Row,
  Col,
  Card,
  Tag,
  Popconfirm,
  message,
  Tabs,
  Badge,
  Tooltip,
  Avatar,
} from 'antd'
import {
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  UserOutlined,
  ReloadOutlined,
  FileProtectOutlined,
  FileAddOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { Lead, LeadStage, ProposalStatus } from '@/types'
import { leadStore } from './leadStore'
import { proposalStore } from '../Proposal/proposalStore'
import { authStore } from '@/context/auth'
import { useMasterData } from '@/context/MasterDataContext'
import TableSkeleton from '@/components/TableSkeleton'
import './LeadList.css'

const STAGE_COLORS: Record<LeadStage, string> = {
  [LeadStage.HOT]:  'red',
  [LeadStage.WARM]: 'gold',
  [LeadStage.COLD]: 'default',
  [LeadStage.LOST]: 'purple',
  [LeadStage.WON]:  'green',
}

const STAGE_EMOJI: Record<LeadStage, string> = {
  [LeadStage.HOT]:  '🔴',
  [LeadStage.WARM]: '🟡',
  [LeadStage.COLD]: '🔵',
  [LeadStage.LOST]: '⚫',
  [LeadStage.WON]:  '🏆',
}

/* ── Component ──────────────────────────────────────── */
const LeadList: React.FC = () => {
  const navigate = useNavigate()
  const { employees } = useMasterData()

  // Current signed-in user drives what leads are visible.
  // Admin / HR → every lead (all, assigned, unassigned). Employee → only leads assigned to them.
  const currentUser = authStore.getUser()
  const roleLC = (currentUser?.role || '').toLowerCase()
  const isFullAccess = roleLC === 'admin' || roleLC.includes('hr')

  const [searchText, setSearchText]   = useState('')
  const [filterStage, setFilterStage] = useState<string>('')
  const [filterCity, setFilterCity]   = useState<string>('')
  const [filterAssignee, setFilterAssignee] = useState<string>('')
  const [filterType, setFilterType]   = useState<string>('')
  const [activeTab, setActiveTab]     = useState<string>(isFullAccess ? 'all' : 'mine')
  const [leads, setLeads]             = useState<Lead[]>(() => leadStore.getLeads())



  // Pull fresh leads & proposals on mount, stay in sync with changes
  useEffect(() => {
    // Refresh both stores
    Promise.all([
      leadStore.refresh(),
      proposalStore.refresh(),
    ]).then(() => {
      setLeads([...leadStore.getLeads()])
    })

    const unSubLead = leadStore.subscribe(() => {
      setLeads([...leadStore.getLeads()])
    })

    const onPropChange = () => {
      setLeads([...leadStore.getLeads()])
    }

    window.addEventListener(proposalStore.eventName, onPropChange)
    return () => {
      unSubLead()
      window.removeEventListener(proposalStore.eventName, onPropChange)
    }
  }, [])

  // Manual stage change handler
  // Deactive is a reminder flag, never a gate — a lead missing its HTSC/GST
  // still moves freely through every stage.
  const handleStageChange = async (leadId: string, newStage: LeadStage) => {
    try {
      await leadStore.saveLeadAndConfirm({ id: leadId, stage: newStage })
      setLeads([...leadStore.getLeads()])
      message.success(`Lead stage updated to "${newStage}"`)
    } catch {
      message.error('Failed to update stage')
    }
  }

  /* ── Role-scoped data ─────────────────────────────── */
  // Employees see only their own leads; admin / HR see every lead.
  const visibleLeads    = isFullAccess ? leads : leads.filter((l) => l.assignedTo === currentUser?.name)
  const myLeads         = visibleLeads.filter((l) => l.assignedTo === currentUser?.name)
  const unassignedLeads = isFullAccess ? leads.filter((l) => !l.assignedTo) : []
  const assignedLeads   = isFullAccess ? leads.filter((l) => !!l.assignedTo) : []

  const getBaseLeads = () => {
    if (activeTab === 'mine')       return myLeads
    if (activeTab === 'unassigned') return unassignedLeads
    if (activeTab === 'assigned')   return assignedLeads
    return visibleLeads
  }

  /* ── Dynamic Filter Options ───────────────────────────── */
  // 1. Dynamic Assignee options (Employee Master + existing Lead assignees)
  const activeEmployees = employees.filter((e) => e.status === 'active')
  const employeesByRole = Object.entries(
    activeEmployees.reduce<Record<string, string[]>>((acc, emp) => {
      const roleName = emp.role || 'Staff'
      if (!acc[roleName]) acc[roleName] = []
      if (emp.name && !acc[roleName].includes(emp.name)) {
        acc[roleName].push(emp.name)
      }
      return acc
    }, {})
  )

  const activeEmpNameSet = new Set(activeEmployees.map((e) => e.name))
  const additionalAssignees = Array.from(
    new Set(
      leads
        .map((l) => l.assignedTo?.trim())
        .filter((name): name is string => Boolean(name && !activeEmpNameSet.has(name)))
    )
  ).sort()

  const assigneeOptions = [
    ...employeesByRole.map(([role, names]) => ({
      label: role,
      options: names.map((name) => ({ label: name, value: name })),
    })),
    ...(additionalAssignees.length > 0
      ? [
          {
            label: employeesByRole.length > 0 ? 'Other Assignees' : 'Lead Assignees',
            options: additionalAssignees.map((name) => ({ label: name, value: name })),
          },
        ]
      : []),
  ]

  // 2. Dynamic Stage options
  const stageOptions = Object.values(LeadStage).map((s) => ({
    label: `${STAGE_EMOJI[s]} ${s}`,
    value: s,
  }))

  // 3. Dynamic Lead Type options
  const leadTypeOptions = Array.from(
    new Set([
      'Consumer',
      'Generator',
      ...leads.map((l) => l.leadType?.trim()).filter((t): t is string => Boolean(t)),
    ])
  ).map((t) => ({
    label: t === 'Consumer' ? '👤 Consumer' : t === 'Generator' ? '⚡ Generator' : t,
    value: t,
  }))

  // 4. Dynamic City options
  const cityOptions = Array.from(
    new Set(
      leads
        .map((l) => l.city?.trim())
        .filter((c): c is string => Boolean(c))
    )
  ).sort().map((c) => ({ label: c, value: c }))

  const filteredLeads = getBaseLeads().filter((lead) => {
    const q = searchText.toLowerCase().trim()
    const matchSearch =
      !q ||
      (lead.id || '').toLowerCase().includes(q) ||
      (lead.companyName || '').toLowerCase().includes(q) ||
      (lead.customerName || '').toLowerCase().includes(q) ||
      (lead.email || '').toLowerCase().includes(q) ||
      (lead.contactNumber || '').includes(q) ||
      (lead.htscNumber || '').toLowerCase().includes(q) ||
      (lead.edcName || '').toLowerCase().includes(q) ||
      (lead.city || '').toLowerCase().includes(q) ||
      (lead.assignedTo || '').toLowerCase().includes(q) ||
      (lead.stage || '').toLowerCase().includes(q) ||
      (lead.leadType || '').toLowerCase().includes(q)

    const matchStage    = !filterStage    || lead.stage === filterStage
    const matchCity     = !filterCity     || lead.city === filterCity
    const matchAssignee = !filterAssignee || lead.assignedTo === filterAssignee
    const matchType     = !filterType     || lead.leadType === filterType
    return matchSearch && matchStage && matchCity && matchAssignee && matchType
  })

  /* ── Columns ──────────────────────────────────────── */
  const columns = [
    {
      title: 'Company',
      dataIndex: 'companyName',
      key: 'companyName',
      render: (text: string, record: Lead) => {
        const isDeactive = record.isDeactive
        const isPending = record.gstStatus === 'pending'
        const missingLabel = (record.missingFields?.length ? record.missingFields : ['HTSC Number', 'GST Number']).join(' & ')
        return (
          <div className="ll-company-cell" onClick={() => navigate(`/leads/${record.id}`)} style={{ cursor: 'pointer' }}>
            <Avatar
              size={32}
              style={{
                background: isDeactive ? '#ef4444' : 'linear-gradient(135deg, #1E3A8A, #2563EB)',
                fontSize: 12, fontWeight: 700, flexShrink: 0,
              }}
            >
              {text.slice(0, 2).toUpperCase()}
            </Avatar>
            <div>
              <div className="ll-company-name" style={{ color: '#1677ff' }}>{text}</div>
              <div className="ll-customer-name" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>{record.customerName}</span>
                {isPending && (
                  <Tooltip
                    title={
                      isDeactive
                        ? `${missingLabel} still missing — the 2-day update window closed on ${record.gstVerifyDueDate || '-'}. This lead is not blocked; please update the numbers.`
                        : `${missingLabel} not yet provided — due ${record.gstVerifyDueDate || '-'} (2 days from lead creation). This lead is not blocked.`
                    }
                  >
                    <Tag color={isDeactive ? 'red' : 'orange'} style={{ fontSize: 10, margin: 0, fontWeight: 700 }}>
                      {isDeactive ? '⚠ OVERDUE' : '⏳ PENDING'} {missingLabel.replace(/ Number/g, '').replace(' & ', '/')}
                    </Tag>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        )
      },
    },
    {
      title: 'Type',
      dataIndex: 'leadType',
      key: 'leadType',
      render: (type: string) => (
        <Tag color={type === 'Consumer' ? 'blue' : 'gold'}>
          {type === 'Consumer' ? '👤' : '⚡'} {type}
        </Tag>
      ),
    },
    {
      title: 'City',
      dataIndex: 'city',
      key: 'city',
      render: (city: string) => <span className="ll-city">{city}</span>,
    },
    {
      title: 'Demand',
      key: 'demand',
      render: (_: any, r: Lead) => (
        <span className="ll-demand">{r.sanctionedDemand} KW</span>
      ),
    },
    {
      title: 'Stage',
      dataIndex: 'stage',
      key: 'stage',
      render: (_: any, record: Lead) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Select
            size="small"
            value={record.stage}
            onChange={(val) => handleStageChange(record.id, val as LeadStage)}
            style={{ minWidth: 165 }}
            bordered={false}
          >
            {Object.values(LeadStage).map((s) => (
              <Select.Option key={s} value={s}>
                <Tag color={STAGE_COLORS[s]} style={{ margin: 0 }}>
                  {STAGE_EMOJI[s]} {s}
                </Tag>
              </Select.Option>
            ))}
          </Select>
        </div>
      ),
    },
    {
      title: 'Proposal',
      key: 'proposal',
      render: (_: any, record: Lead) => {
        const activeProp = proposalStore.getAll().find(
          (p) =>
            (p.leadId === record.id ||
              (p.leadName && p.leadName.trim().toLowerCase() === record.companyName.trim().toLowerCase())) &&
            p.status !== ProposalStatus.REJECTED &&
            p.status !== ProposalStatus.EXPIRED
        )

        if (!record.convertedToProposal && !activeProp) {
          return <span style={{ color: '#bbb' }}>—</span>
        }

        return (
          <Tooltip title={activeProp ? `Proposal ${activeProp.referenceNumber || activeProp.id} (${activeProp.status})` : 'Converted to Proposal'}>
            <Tag
              color="cyan"
              style={{ cursor: activeProp ? 'pointer' : 'default', fontSize: 11, margin: 0, fontWeight: 600 }}
              onClick={(e) => {
                if (activeProp) {
                  e.stopPropagation()
                  navigate(`/proposals/${activeProp.id}`)
                }
              }}
            >
              📄 Converted to Proposal {activeProp ? `(${activeProp.referenceNumber || activeProp.id})` : ''}
            </Tag>
          </Tooltip>
        )
      },
    },
    {
      title: 'Assigned To',
      dataIndex: 'assignedTo',
      key: 'assignedTo',
      render: (user: string) =>
        user ? (
          <Space size={6}>
            <Avatar size={22} icon={<UserOutlined />} style={{ background: '#1E3A8A' }} />
            <span>{user}</span>
          </Space>
        ) : (
          <Tag color="orange">Unassigned</Tag>
        ),
    },
    {
      // Newest-created lead first. defaultSortOrder makes this an explicit,
      // guaranteed sort (shown with the sort arrow) rather than something
      // that only happens to look right because of insertion/fetch order —
      // and sorting on the full timestamp (not just the date) is what keeps
      // several leads created the same day in true creation order.
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      defaultSortOrder: 'descend' as const,
      sorter: (a: Lead, b: Lead) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      render: (d: Date) =>
        new Date(d).toLocaleString('en-IN', {
          day: '2-digit', month: 'short', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        }),
    },
    {
      title: 'Action',
      key: 'action',
      width: 140,
      render: (_: any, record: Lead) => {
        const activeProp = proposalStore.getAll().find(
          (p) =>
            (p.leadId === record.id ||
              (p.leadName && p.leadName.trim().toLowerCase() === record.companyName.trim().toLowerCase())) &&
            p.status !== ProposalStatus.REJECTED &&
            p.status !== ProposalStatus.EXPIRED
        )
        return (
          <Space size={4} onClick={(e) => e.stopPropagation()}>
            <Tooltip title="View Lead Details">
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => navigate(`/leads/${record.id}`)}
              />
            </Tooltip>
            {activeProp ? (
              <Tooltip title={`View Proposal (${activeProp.referenceNumber || activeProp.id})`}>
                <Button
                  type="link"
                  size="small"
                  icon={<FileProtectOutlined />}
                  style={{ color: '#00A651' }}
                  onClick={() => navigate(`/proposals/${activeProp.id}`)}
                />
              </Tooltip>
            ) : (
              // Proposal creation is always allowed, even while the lead is
              // Deactive or missing HTSC/GST.
              <Tooltip title="Create Proposal for Lead">
                <Button
                  type="link"
                  size="small"
                  icon={<FileAddOutlined />}
                  style={{ color: '#00A651' }}
                  onClick={() => navigate('/proposals/new', { state: { fromLead: record } })}
                />
              </Tooltip>
            )}
            <Popconfirm
              title="Delete Lead"
              description="Are you sure? This cannot be undone."
              onConfirm={async () => {
                try {
                  await leadStore.deleteLead(record.id)
                  setLeads(leadStore.getLeads())
                  message.success('Lead deleted')
                } catch {
                  setLeads(leadStore.getLeads())
                  message.error('Could not delete lead — please sign out and log in again.')
                }
              }}
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Delete">
                <Button type="link" danger size="small" icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          </Space>
        )
      },
    },
  ]

  /* ── Render ────────────────────────────────────────── */
  if (!leadStore.loaded || !proposalStore.loaded) return <TableSkeleton columns={6} />

  return (
    <div className="lead-list-container">

      {/* Page Header */}
      <div className="ll-header">
        <div>
          <h2>Lead Management</h2>
          <p className="ll-header-sub">
            Capture, assign, qualify and track leads through their lifecycle.
          </p>
          <div style={{ marginTop: 4, fontSize: 12, color: '#64748b' }}>
            Signed in as <strong style={{ color: '#1E3A8A' }}>{currentUser?.name}</strong>
            <Tag color={isFullAccess ? 'blue' : 'green'} style={{ marginLeft: 8 }}>
              {isFullAccess ? `${currentUser?.role || 'Admin'} · all leads` : 'Staff · my leads only'}
            </Tag>
          </div>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={async () => {
            await leadStore.refresh()
            setLeads(leadStore.getLeads())
            message.info('Leads list refreshed')
          }} />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/leads/new')}
          >
            Add New Lead
          </Button>
        </Space>
      </div>

      {/* Summary tabs — role-aware. Admin: All + Unassigned. Employee: My Leads only. */}
      <div className="ll-tabs-bar">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="small"
          items={isFullAccess ? [
            {
              key: 'all',
              label: (
                <span>All Leads <Badge count={leads.length} style={{ backgroundColor: '#1E3A8A', marginLeft: 4 }} /></span>
              ),
            },
            {
              key: 'assigned',
              label: (
                <span>
                  Assigned{' '}
                  {assignedLeads.length > 0 && (
                    <Badge count={assignedLeads.length} style={{ backgroundColor: '#2563EB', marginLeft: 4 }} />
                  )}
                </span>
              ),
            },
            {
              key: 'unassigned',
              label: (
                <span>
                  Unassigned{' '}
                  {unassignedLeads.length > 0 && (
                    <Badge count={unassignedLeads.length} style={{ backgroundColor: '#D97706', marginLeft: 4 }} />
                  )}
                </span>
              ),
            },
          ] : [
            {
              key: 'mine',
              label: (
                <span>My Leads <Badge count={myLeads.length} style={{ backgroundColor: '#2563EB', marginLeft: 4 }} /></span>
              ),
            },
          ]}
        />
      </div>

      {/* Filters */}
      <Card className="ll-filter-card">
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} md={6}>
            <Input.Search
              placeholder="Search lead no., company, customer, HTSC..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Stage"
              allowClear
              value={filterStage || undefined}
              onChange={setFilterStage}
              style={{ width: '100%' }}
              options={stageOptions}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Lead Type"
              allowClear
              value={filterType || undefined}
              onChange={setFilterType}
              style={{ width: '100%' }}
              options={leadTypeOptions}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Assigned To"
              allowClear
              value={filterAssignee || undefined}
              onChange={setFilterAssignee}
              style={{ width: '100%' }}
              options={assigneeOptions}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="City"
              allowClear
              value={filterCity || undefined}
              onChange={setFilterCity}
              style={{ width: '100%' }}
              options={cityOptions}
            />
          </Col>
          <Col xs={24} sm={12} md={2}>
            <Button
              block
              onClick={() => {
                setSearchText('')
                setFilterStage('')
                setFilterCity('')
                setFilterAssignee('')
                setFilterType('')
              }}
            >
              Clear
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Card>
        <div className="ll-results-info">
          Showing <strong>{filteredLeads.length}</strong> of{' '}
          <strong>{getBaseLeads().length}</strong> leads
        </div>
        <Table
          dataSource={filteredLeads}
          columns={columns}
          rowKey="id"
          pagination={{
            defaultPageSize: 10,
            pageSizeOptions: ['10', '25', '50', '100'],
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} leads`,
          }}
          rowClassName={(record) =>
            record.stage === LeadStage.LOST ? 'll-row-lost' : ''
          }
        />
      </Card>
    </div>
  )
}

export default LeadList
